import axios from "axios";
import { triggerSessionAlert } from "../utils/SessionAlertHelper";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const getCustomersData = async (formData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/customers`, formData, {
      withCredentials: true,
    });

    return {
      success: response.data.success,
      data: response.data.data.customers,
      totalPages: response.data.data.totalPages,
      currentPage: response.data.data.currentPage,
      totalCount: response.data.data.totalCustomers,
    };
  } catch (error) {
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message:
          "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status === 403) {
      triggerSessionAlert("Sorry, you do not have permission.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while fetching customers.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

export const getCustomersDataForExport = async (formData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/customers/export`,
      formData,
      {
        withCredentials: true,
      }
    );

    return {
      success: response.data.success,
      data: response.data.data.customers, // Export only 'customers' array
      totalCount: response.data.data.totalCustomers,
    };
  } catch (error) {
    // Network errors (server unreachable)
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message:
          "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }

    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }

    if (error.response?.status === 403) {
      triggerSessionAlert("Sorry, you do not have permission.");
      return;
    }

    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }

    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while fetching customers.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

export const updateCustomer = async (formData) => {
  try {
    const payload = {
      customerId: formData.get("customerId"),
    };

    const name = formData.get("name");
    const email = formData.get("email");

    if (name || name === "") payload.name = name;
    if (email || email === "") payload.email = email;

    const response = await axios.put(
      `${API_BASE_URL}/customers/update`,
      payload,
      {
        withCredentials: true,
      }
    );

    return {
      success: response.data.success,
      message: response.data.message || "Customer updated successfully",
      data: response.data.data.customer,
    };
  } catch (error) {
    // Network errors (server unreachable)
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message:
          "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status === 403) {
      triggerSessionAlert("Sorry, you do not have permission.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while updating customer.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

export const updateCustomerStatus = async (customerId, status) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/customers/update/status`,
      {
        customerId,
        status,
      },
      {
        withCredentials: true,
      }
    );

    return {
      success: response.data.success,
      message: response.data.message || "Customer status updated successfully",
      data: response.data.data.customer,
    };
  } catch (error) {
    // Network errors (server unreachable)
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message:
          "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status === 403) {
      triggerSessionAlert("Sorry, you do not have permission.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while updating customer status.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

export const deleteCustomer = async (customerId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/customers/delete?&customerId=${customerId}`,
      {
        withCredentials: true,
      }
    );

    return {
      success: response.data.success,
      message: response.data.message || "Customer deleted successfully",
    };
  } catch (error) {
    // Network errors (server unreachable)
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message:
          "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status === 403) {
      triggerSessionAlert("Sorry, you do not have permission.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while deleting customer.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

export const getReferralCodes = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/referral/only`, {
      withCredentials: true,
    });

    return {
      success: response.data.success,
      message: response.data.message,
      referralCodes: response.data.data.referrals.map((ref) => ({
        id: ref.id,
        code: ref.code,
      })),
    };
  } catch (error) {
    if (!error.response) {
      return {
        success: false,
        message: "Cannot connect to the server. Please try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }

    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while fetching referral codes.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later.",
      },
    };
  }
};
